import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import {
  IPromoCode,
  useCreatePromoCodeMutation,
  useDeletePromoCodeMutation,
  useLazyGetPromoCodesQuery,
  useUpdatePromoCodeMutation,
} from 'app/services/promocodes';
import { AddIcon, TrashCanSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import ToggleButton from 'Components/Buttons/ToggleButton';
import { USDate } from 'Components/Dates/USDate';
import { CheckboxInputBasic } from 'Components/Form/Basic/CheckBoxInputBasic';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import { SingleDateInput } from 'Components/Form/Inputs/DateInput';
import Label from 'Components/Form/Label';
import { Grid } from 'Components/Grid';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import usePagination from 'Components/Pagination/usePagination';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import useCreateModal from 'Components/Popups/useCreateModal';
import Spinner from 'Components/Spinner';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

function PromoCodeStatus({ promocode }: { promocode: IPromoCode }) {
  const nowTimestamp = new Date().getTime();
  const from = new Date(promocode.valid_from);
  const to = new Date(promocode.valid_to);
  const fromTimesTamp = from.getTime();
  const toTimestamp = to.getTime();

  if (nowTimestamp > toTimestamp) return <> Expired</>;
  if (fromTimesTamp > nowTimestamp) return <>Pending</>;

  return <>Active</>;
}

export default function PromoCodesList() {
  const {
    data,
    Pagination,
    refetchAfterDelete,
    refetchSamePage,
    goToFirstPage,
  } = usePagination(useLazyGetPromoCodesQuery, {
    filters: {},
    per_page: 10,
  });
  const CreateModal = useCreateModal();
  const confirmDelete = useConfirmDeleteModal();
  const [DeletePromoCodeTrigger] = useDeletePromoCodeMutation();
  const [UpdateTrigger, { isLoading: isUpdating }] =
    useUpdatePromoCodeMutation();
  const [CreateTrigger, { isLoading: isCreating }] =
    useCreatePromoCodeMutation();
  const UpdateModal = useCreateModal<IPromoCode>();

  const updateHandle = (pr: IPromoCode) => {
    UpdateTrigger(pr)
      .unwrap()
      .then(() => {
        successToast('Promocode updated');
        refetchSamePage();
        UpdateModal.close();
      })
      .catch((e) => {
        errorToast(e.data.data[0]);
      });
  };

  const createHandle = (pr: IPromoCode) => {
    CreateTrigger(pr)
      .unwrap()
      .then(() => {
        successToast('Promocode created');
        goToFirstPage();
        CreateModal.close();
      })
      .catch((e) => {
        errorToast(e.data.data[0]);
      });
  };

  if (!data) return <Spinner />;
  return (
    <ScrollContainer>
      <DashboardContainerHeader title="Promo Codes">
        <Button className="max-w-min" onClick={CreateModal.toggle}>
          <AddIcon /> Add
        </Button>
      </DashboardContainerHeader>
      <ScrollContainer>
        <Grid
          onRowClick={({ data }) => UpdateModal.openWithContext({ data })}
          data={data}
          columns={[
            'Code',
            'Discount',
            'Type',
            'Active Period',
            'Status',
            { render: '', width: '70px' },
          ]}
          renderCells={(pc) => [
            <div
              onClick={(e) => e.stopPropagation()}
              className="normal-case font-semibold select-text"
            >
              {pc.code}
            </div>,
            `${pc.percentage}% OFF`,
            pc.is_one_use ? 'Single Use' : 'Multiple Use',
            <div>
              <USDate date={pc.valid_from} /> - <USDate date={pc.valid_to} />
            </div>,
            <PromoCodeStatus promocode={pc} />,
            <Button
              className="max-w-min"
              theme="red-outline"
              onClick={(e) => {
                e.stopPropagation();
                confirmDelete('Promo Code', () => {
                  return DeletePromoCodeTrigger(pc.id)
                    .unwrap()
                    .then(refetchAfterDelete);
                });
              }}
            >
              <TrashCanSolidIcon />
            </Button>,
          ]}
        />
      </ScrollContainer>
      <Pagination />

      <CreateModal.Component>
        <OverlayWindow title="Add Promo Code" onClose={CreateModal.close}>
          <PromoCodeForm
            onSubmit={createHandle}
            isLoading={isCreating}
            onCancel={CreateModal.close}
          />
        </OverlayWindow>
      </CreateModal.Component>

      <UpdateModal.Component>
        {({ data }) => (
          <OverlayWindow title={'Edit Promo Code'} onClose={UpdateModal.close}>
            <PromoCodeForm
              value={data}
              isLoading={isUpdating}
              onSubmit={updateHandle}
              onCancel={UpdateModal.close}
            />
          </OverlayWindow>
        )}
      </UpdateModal.Component>
    </ScrollContainer>
  );
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .nullable()
    .required('Required')
    .matches(
      /^[\w-]+$/,
      'Promo code must contain only letters, numbers, dashes, and underscores',
    ),
  percentage: Yup.number()
    .typeError('Amount must be a number')
    .nullable()
    .required('Required')
    .max(100)
    .min(0),
  valid_from: Yup.string().nullable().required('Required'),
  valid_to: Yup.string().nullable().required('Required'),
  is_one_use: Yup.boolean().nullable().required('Required'),
});

function PromoCodeForm({
  value,
  onSubmit,
  onCancel,
  isLoading,
}: {
  isLoading?: boolean;
  value?: IPromoCode;
  onSubmit: (p: IPromoCode) => void;
  onCancel: () => void;
}) {
  let {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    control,
  } = useForm<IPromoCode>({
    resolver: validationSchema && yupResolver(validationSchema),
    defaultValues: {
      ...value,
      is_one_use: value?.is_one_use === undefined ? true : value.is_one_use,
    },
  });

  const validFrom = watch('valid_from');
  const validTo = watch('valid_to');

  return (
    <>
      <form
        id="cp_form"
        onSubmit={handleSubmit(onSubmit)}
        className="h-full flex-1 w-full grid gap-2 mb-2 overflow-y-auto"
      >
        <TextInputBasic
          register={register}
          name="code"
          type="text"
          placeholder="Code"
          label="Code"
          errors={errors}
        />

        <TextInputBasic
          register={register}
          name="percentage"
          type="number"
          placeholder="Discount Percentage"
          label="Discount Percentage"
          errors={errors}
        />

        <CheckboxInputBasic
          register={register}
          name="is_one_use"
          placeholder="Single Use"
          label="Single Use Promo Code"
          errors={errors}
        />

        <Controller
          name="valid_from"
          control={control}
          render={({ field }) => (
            <>
              <Label label="From" />
              <SingleDateInput
                minDate={moment().subtract(1, 'days').toDate()}
                maxDate={validTo ? validTo : undefined}
                onChangeStringDate={field.onChange}
                value={validFrom}
                hourOffset="min"
                format="UTC"
              />
              <ErrorMessage
                errors={errors}
                name="valid_from"
                render={({ message }) => (
                  <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 pl-4">
                    {message}
                  </p>
                )}
              />
            </>
          )}
        />

        <Controller
          name="valid_to"
          control={control}
          render={({ field }) => (
            <>
              <Label label="To" />
              <SingleDateInput
                minDate={validFrom ? validFrom : undefined}
                onChangeStringDate={field.onChange}
                value={validTo}
                hourOffset="max"
                format="UTC"
              />
              <ErrorMessage
                errors={errors}
                name="valid_to"
                render={({ message }) => (
                  <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                    {message}
                  </p>
                )}
              />
            </>
          )}
        />
      </form>
      <div className="flex flex-1 justify-end shadow-top border-gray-100 p-2 mt-2 gap-2">
        <Button type="button" onClick={onCancel} className="max-w-min">
          Cancel
        </Button>
        <Button
          isLoading={isLoading}
          form="cp_form"
          type="submit"
          className="max-w-min"
          theme="green-fill"
        >
          Save
        </Button>
      </div>
    </>
  );
}
